import type { PortfolioCompanyDto } from '@/services/bff/portfolio.api';
import type {
  Company,
  ExitManagementState,
  ExitOpportunity,
  ExitProject,
  Fund,
} from './types';

const mapPortfolioStatus = (
  status: string,
): Company['postInvestmentStatus'] => {
  if (status === '已退出') return '已退出';
  if (status === '退出准备') return '退出推进中';
  return '持有中';
};

export const mapPortfolioCompanyToExit = (
  company: PortfolioCompanyDto,
): Company => ({
  id: company.id,
  fundUuid: company.fundUuid ?? undefined,
  name: company.name,
  shortName: company.shortName || company.name,
  industry: company.industry,
  foundedAt: company.investedAt,
  latestValuation: company.valuation,
  postInvestmentStatus: mapPortfolioStatus(company.status),
  operatingSummary: company.summary || company.tagline || '暂无经营摘要',
});

export const resolveExitDistributionFundUuid = (
  project?: Pick<ExitProject, 'fundUuid'>,
  company?: Pick<Company, 'fundUuid'>,
) => project?.fundUuid || company?.fundUuid;

export const buildFundsFromPortfolio = (
  companies: PortfolioCompanyDto[],
  opportunities: ExitOpportunity[],
  projects: ExitProject[],
): Record<string, Fund> => {
  const funds: Record<string, Fund> = {};

  const upsert = (id?: string, name?: string) => {
    const fundId = id?.trim();
    if (!fundId) return;
    const existing = funds[fundId];
    const label = name?.trim();
    funds[fundId] = {
      id: fundId,
      name: label || existing?.name || fundId,
      remainingMonths: existing?.remainingMonths ?? 24,
    };
  };

  companies.forEach((item) => {
    upsert(item.fundUuid || item.fundId, item.fundNameSnapshot || item.fundId);
  });
  opportunities.forEach((item) => {
    upsert(item.fundUuid || item.fundId);
  });
  projects.forEach((item) => {
    upsert(item.fundUuid || item.fundId);
  });

  return funds;
};

export const resolveExitCompany = (
  state: ExitManagementState,
  companyId: string,
): Company =>
  state.companies[companyId] ?? {
    id: companyId,
    name: companyId,
    shortName: companyId.slice(0, 8),
    industry: '-',
    foundedAt: '-',
    latestValuation: 0,
    postInvestmentStatus: '持有中',
    operatingSummary: '暂无经营摘要',
  };

export const resolveExitFund = (state: ExitManagementState, fundId: string): Fund =>
  state.funds[fundId] ?? {
    id: fundId,
    name: fundId,
    remainingMonths: 0,
  };

export const findExitProjectByOpportunity = (
  state: ExitManagementState,
  opportunityId: string,
) =>
  Object.values(state.projects).find(
    (project) => project.opportunityId === opportunityId,
  );
